import { localQueryEntityOptions } from '@app/icare/client/db/client/localQueryOneOptions'
import { sync } from '@app/icare/client/sync'
import { PatientFormId } from '@app/icare/db/schema/form/PatientFormCommon'
import { PatientOrderFormType } from '@app/icare/db/schema/form/PatientFormType'
import { getFormHandler } from '@app/icare/documents/config'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import { PatientTherapyId } from '@app/icare/entities/patient/schema/PatientTherapy'
import type { PatientOrderForm } from '@app/icare/entities/patientForm/schema/PatientForm'
import { appFormOptions } from '@app/icare/form/appFormOptions'
import { useAppForm } from '@app/icare/form/useAppForm'
import { t } from '@lingui/core/macro'
import { zodParams } from '@pkg/db/utils'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'
import { ConfigProvider, Form } from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'
import { z } from 'zod'
import { EditOrder } from './-edit'

const SearchParams = z.object({
  therapyId: PatientTherapyId.ref.nullish(),
  order: z.enum(PatientOrderFormType).nullish(),
})

export const Route = createFileRoute('/_app/patienten/$id/_edit/_documents/_forms/bedarfsermittlung/$form')({
  component: RouteComponent,
  params: zodParams(z.object({ id: PatientId.ref, form: PatientFormId.ref })),
  validateSearch: zodValidator(SearchParams),
  loader: async ({ params, context: { queryClient } }) => {
    await queryClient.ensureQueryData(localQueryEntityOptions(params.id))
    await queryClient.ensureQueryData(localQueryEntityOptions(params.form))
  },
  staticData: {
    layout: {
      menuRender() {
        return <div />
      },
    },
  },
})

function RouteComponent() {
  const { form: formId } = Route.useParams()
  const { order } = Route.useSearch()
  const [isEditing, setIsEditing] = useState(false)

  const current = useQuery(localQueryEntityOptions(formId))
  const { validationSchema } = getFormHandler(current.data?.type ?? order)
  const currentData = current.data as unknown as PatientOrderForm
  const defaultValues = {
    ...currentData,
    statusChangedAt: currentData?.statusChangedAt ? dayjs(currentData.statusChangedAt).toISOString() : null,
  } as PatientOrderForm

  const form = useAppForm(
    appFormOptions(validationSchema, {
      defaultValues,
      toast: { subject: t`Bedarfsermittlung` },
      onSubmit: async ({ value }) => {
        await sync.mutate(value)
        form.reset(value)
      },
    }),
  )

  const editModeAllowed = form.state.values.status === 'processing'

  const editablePolicy = !editModeAllowed
    ? { forceMode: 'read' as const }
    : { forceMode: isEditing ? ('edit' as const) : ('read' as const) }
  const isFormInEditMode = editablePolicy.forceMode === 'edit'

  return (
    <form.AppForm>
      <form.ProForm editablePolicy={editablePolicy}>
        <ConfigProvider
          theme={{
            components: {
              Form: {
                itemMarginBottom: isFormInEditMode ? 10 : 0,
              },
            },
          }}
        >
          <Form component={false}>
            <EditOrder isEditing={isEditing} onEditModeChange={setIsEditing} editModeAllowed={editModeAllowed} />
          </Form>
        </ConfigProvider>
      </form.ProForm>
    </form.AppForm>
  )
}
